/* eslint-disable import-x/export */
//  We export certain components multiple times because we export all
//   of Chakra's components and then override some of them.
import { keyframes } from '@emotion/react'
import { omit } from 'lodash'
import * as React from 'react'
import {
  chakra,
  AccordionPanel as ChakraAccordionPanel,
  Badge as ChakraBadge,
  AccordionPanelProps,
  Box,
  ButtonProps as ChakraButtonProps,
  Button as ChakraButton,
  Checkbox as ChakraCheckbox,
  CheckboxProps,
  DrawerBody as ChakraDrawerBody,
  DrawerContent as ChakraDrawerContent,
  DrawerContentProps,
  FormControl as ChakraFormControl,
  FormControlProps as ChakraFormControlProps,
  FormErrorMessage as ChakraFormErrorMessage,
  FormErrorMessageProps,
  forwardRef,
  MenuItem as ChakraMenuItem,
  MenuItemProps as ChakraMenuItemProps,
  Heading as ChakraHeading,
  HeadingProps,
  Icon,
  IconButton as ChakraIconButton,
  IconButtonProps,
  Input as ChakraInput,
  InputProps as ChakraInputProps,
  ModalBody as ChakraModalBody,
  ModalBodyProps,
  ModalContent as ChakraModalContent,
  ModalContentProps,
  Radio as ChakraRadio,
  RadioProps as ChakraRadioProps,
  Select as ChakraSelect,
  SelectProps,
  Skeleton as ChakraSkeleton,
  Switch as ChakraSwitch,
  SwitchProps,
  TabPanel as ChakraTabPanel,
  TabPanelProps,
  Text as ChakraText,
  TextProps as ChakraTextProps,
  Textarea as ChakraTextarea,
  TextareaProps as ChakraTextareaProps,
  Tooltip as ChakraTooltip,
  TooltipProps as ChakraTooltipProps,
  useMultiStyleConfig,
  SkeletonProps,
  useFormControlProps
} from '@chakra-ui/react'
import { CaretDown, Check, Minus, WarningO } from '~/icons'
import { mergeRefs } from '~/common/reactUtils'
import { RegionErrorBoundary } from '~/design_system/components/empty_states/EmptyStates'
import {
  ExtendedFormControlContextProps,
  FormControlExtendedContextProvider,
  useFormControlExtendedContext
} from './components/form_controls/FormControlExtendedContext'
import { useFormAlertRegistrationRef } from './components/form_alert_list/FormAlertListContext'
import { asLink } from './components/links/Links'

/* Chakra Components */
export * from '@chakra-ui/react'

/* Chakra Passthroughs with VNDLY UX guidance */
//
// These const overrides re-export Chakra components under the same name
// so we can attach @agent-* JSDocs that surface in vndly-ux's AGENTS.md.
// The const override pattern matches the customized-components style below
// and avoids the duplicate-named-export conflict that strict-mode bundlers
// (esbuild via Playwright CT) flag against `export { X } from '@chakra-ui/react'`.
//
// Convention reference: docs/frontend/agent-jsdoc-tags.md

/**
 * @agent-preferred
 * @agent-doc vndly/assets/js/design_system/foundations/data_display/Badge.mdx
 * @agent-gotcha status badge, NOT a generic label. REQUIRES variant + size.
 * @agent-gotcha variant: success | pending | complete | incomplete | danger | redlinePrevious | redlineUpdated. size: sm | lg.
 * @agent-gotcha NEVER use colorScheme, textTransform, or Chakra defaults (subtle/outline/solid).
 * @agent-gotcha For non-status pills, use a different component.
 */
export const Badge = ChakraBadge

/* Customized Chakra Components */

/**
 * VNDLY Input
 *
 * Input that allows users enter single valued data.
 *
 * Extended Chakra's props with `isWarned` prop to show warning message.
 *
 * See: https://v1.chakra-ui.com/docs/components/form/input
 *
 * @agent-gotcha pair with FormControl for labels, errors, and warnings
 */
export const Input = forwardRef<ChakraInputProps & ExtendedFormControlContextProps, 'input'>((props, ref) => {
  const { getInputProps } = useFormControlExtendedContext()
  return <ChakraInput ref={ref} {...props} {...getInputProps?.(props)} />
})

Input.displayName = 'Input'

// Input.id is used internally by Chakra when InputGroup clones the child Input
// without it, the styles are not cloned properly.
Input.id = 'Input'

/**
 * Text is the used to render text and paragraphs within an interface
 *
 * This override is only here to fix a typescript bug in Chakra 2.0.2.
 * It should be removed when we upgrade Chakra to see if the problem goes away
 *
 * TODO: Fixed isTruncated here because of a type bug in Chakra
 * See: https://v2.chakra-ui.com/docs/components/text
 */
export const Text = forwardRef<Omit<ChakraTextProps, 'children'> & { children?: any; isTruncated?: boolean }, 'p'>(
  ({ isTruncated, ...props }, ref) => {
    return (
      <ChakraText
        ref={ref}
        noOfLines={isTruncated ? 1 : undefined}
        wordBreak={isTruncated ? 'break-all' : 'normal'}
        {...props}
      />
    )
  }
)

Text.displayName = 'Text'

export const AccordionPanel = forwardRef<AccordionPanelProps, 'div'>(({ children, ...props }, ref) => {
  return (
    <ChakraAccordionPanel ref={ref} {...props}>
      <RegionErrorBoundary>{children}</RegionErrorBoundary>
    </ChakraAccordionPanel>
  )
})

AccordionPanel.displayName = 'AccordionPanel'

// This is just based on testing the drawer out on a zoom level of 400%.  600 seems to give enough
//  room for the user to actually see the content well.
const SHORT_SCREEN_HEIGHT = 600

export const DrawerBody = forwardRef<ModalBodyProps, 'div'>(({ children, ...props }, ref) => {
  return (
    <ChakraDrawerBody
      ref={ref}
      {...props}
      sx={{
        ...props.sx,
        // On short screens we don't flex because it causes issues with scrolling and content being cut off
        [`@media screen and (max-height: ${SHORT_SCREEN_HEIGHT}px)`]: {
          flex: 'none'
        }
      }}>
      <RegionErrorBoundary>{children}</RegionErrorBoundary>
    </ChakraDrawerBody>
  )
})

DrawerBody.displayName = 'DrawerBody'

export const DrawerContent = forwardRef<DrawerContentProps, 'div'>(({ children, ...props }, ref) => {
  return (
    <ChakraDrawerContent
      ref={ref}
      {...props}
      sx={{
        // On short screens we don't flex because it causes issues with scrolling and content being cut off
        [`@media screen and (max-height: ${SHORT_SCREEN_HEIGHT}px)`]: {
          overflowY: 'auto'
        }
      }}>
      <RegionErrorBoundary>{children}</RegionErrorBoundary>
    </ChakraDrawerContent>
  )
})

DrawerContent.displayName = 'DrawerContent'

export const ModalBody = forwardRef<ModalBodyProps, 'div'>(({ children, ...props }, ref) => {
  return (
    <ChakraModalBody
      ref={ref}
      {...props}
      sx={{
        ...props.sx,
        // On short screens we don't flex because it causes issues with scrolling and content being cut off
        [`@media screen and (max-height: ${SHORT_SCREEN_HEIGHT}px)`]: {
          flex: 'none'
        }
      }}>
      <RegionErrorBoundary>{children}</RegionErrorBoundary>
    </ChakraModalBody>
  )
})

ModalBody.displayName = 'ModalBody'

export const ModalContent = forwardRef<ModalContentProps, 'div'>(({ children, ...props }, ref) => {
  return (
    <ChakraModalContent
      ref={ref}
      {...props}
      sx={{
        // On short screens we don't flex because it causes issues with scrolling and content being cut off
        [`@media screen and (max-height: ${SHORT_SCREEN_HEIGHT}px)`]: {
          overflowY: 'auto',
          top: '-1rem' // on short screens the top of the modal is too low on the page so this moves it up
        }
      }}>
      <RegionErrorBoundary>{children}</RegionErrorBoundary>
    </ChakraModalContent>
  )
})

ModalContent.displayName = 'ModalContent'

export const TabPanel = forwardRef<TabPanelProps, 'div'>(({ children, ...props }, ref) => {
  return (
    <ChakraTabPanel ref={ref} {...props}>
      <RegionErrorBoundary>{children}</RegionErrorBoundary>
    </ChakraTabPanel>
  )
})

TabPanel.displayName = 'TabPanel'

/**
 * VNDLY Textarea
 *
 * Textarea is used to enter an amount of text that's longer than a single line
 * Extended Chakra's props with `isWarned` prop to show warning message.
 *
 * See: https://v1.chakra-ui.com/docs/components/form/textarea
 *
 * @agent-gotcha pair with FormControl for labels, errors, and warnings
 */
export const Textarea = forwardRef<ChakraTextareaProps & ExtendedFormControlContextProps, 'textarea'>((props, ref) => {
  const { getInputProps } = useFormControlExtendedContext()
  return <ChakraTextarea ref={ref} {...props} {...getInputProps?.(props)} />
})

Textarea.displayName = 'Textarea'

const CustomCheckboxIcon = (props: any) => {
  const { isIndeterminate, isChecked, ...rest } = props

  const icon = isIndeterminate ? Minus : Check
  return <Icon as={icon} w={6} h={6} opacity={Number(isChecked)} {...rest} />
}

/**
 * Checkbox
 *
 * React component used in forms when a user needs to select
 * multiple values from several options.
 *
 * Extended with custom icon and `isWarned` prop to show warning message.
 *
 * See: https://v1.chakra-ui.com/docs/components/form/checkbox
 */
export const Checkbox = React.memo(
  forwardRef<CheckboxProps & ExtendedFormControlContextProps, 'input'>(({ ...props }, ref) => {
    const { getInputProps } = useFormControlExtendedContext()
    return (
      <ChakraCheckbox data-checkbox icon={<CustomCheckboxIcon />} ref={ref} {...props} {...getInputProps?.(props)} />
    )
  })
)

Checkbox.displayName = 'Checkbox'

/**
 * VNDLY Radio
 *
 * Component is used in forms when a user needs to select a single value from
 * several options.
 *
 * Extended with `isWarned` prop to show warning message.
 *
 * See: https://v1.chakra-ui.com/docs/components/form/radio
 */
export const Radio = forwardRef<ChakraRadioProps & ExtendedFormControlContextProps, 'input'>((props, ref) => {
  const { 'aria-describedby': ariaDescribedBy } = useFormControlProps(props)
  const { getInputProps } = useFormControlExtendedContext()
  return <ChakraRadio ref={ref} {...props} {...getInputProps?.({ ...props, 'aria-describedby': ariaDescribedBy })} />
})

Radio.displayName = 'Radio'

/**
 * VNDLY Select Dropdown
 *
 * Using the default Chakra Select with custom icon. This component should be used instead of Chakra's Select
 * for dropdown inputs.
 * See http://storybook.beta.vndly.com/?path=/docs/foundations-forms-selectors--select for
 * examples and usage.
 *
 * See: https://v1.chakra-ui.com/docs/components/form/select
 *
 * @agent-gotcha pair with FormControl for labels, errors, and warnings
 */
export const Select = forwardRef<SelectProps, 'select'>((props, ref) => {
  const { getInputProps } = useFormControlExtendedContext()
  return <ChakraSelect icon={<CaretDown />} ref={ref} {...props} {...getInputProps?.(props)} />
})

Select.displayName = 'Select'

/**
 * VNDLY Switch Dropdown
 *
 * Extended Chakra's Switch with `isWarned` prop to show warning message.
 *
 * See: https://v1.chakra-ui.com/docs/components/form/switch
 */
export const Switch = forwardRef<SwitchProps & ExtendedFormControlContextProps, 'input'>((props, ref) => {
  const { getInputProps } = useFormControlExtendedContext()
  return <ChakraSwitch ref={ref} {...props} {...getInputProps?.(props)} />
})

Switch.displayName = 'Switch'

/**
 * Form Error Message
 *
 * Error messages help users diagnose and fix problems with a users input.  Error states should not
 * be activated until after a user has interacted with the form control.
 *
 * Meant to be used in conjunction with the `isInvalid` prop on the `FormControl` component.
 *
 * Using the default Chakra FormErrorMessage with custom icon and styling. This component should be used instead
 * of Chakra's FormErrorMessage for inputs.
 *
 * See: https://v1.chakra-ui.com/docs/components/form/form-control
 *
 * @agent-gotcha only shows correctly with FormControl isInvalid
 */
export const FormErrorMessage = forwardRef(
  // TODO: We are redefining children here because of a type bug in formik
  ({ children, ...props }: Omit<FormErrorMessageProps, 'textStyle' | 'children'> & { children?: any }, ref) => {
    const styles = useMultiStyleConfig('FormError', props)
    return (
      <ChakraFormErrorMessage {...props} ref={ref}>
        <WarningO __css={styles?.icon} aria-label="Form error message icon" />
        <chakra.span className="chakra-form__error-message-text">{children}</chakra.span>
      </ChakraFormErrorMessage>
    )
  }
)

FormErrorMessage.displayName = 'FormErrorMessage'

export type ExtendedFormControlProps = ChakraFormControlProps & ExtendedFormControlContextProps

/**
 * Overrides Chakra's FormControl component to provide additional context for form alerts.
 *
 * See: https://v1.chakra-ui.com/docs/components/form/form-control
 *
 * @agent-gotcha wrap labeled/validated fields here instead of styling labels by hand
 */
export const FormControl = forwardRef<ExtendedFormControlProps, 'div'>(({ children, ...props }, ref) => {
  // If the field has an id, it will be registered with the form alert list
  const registerAlertRef = useFormAlertRegistrationRef(props)

  return (
    <ChakraFormControl ref={mergeRefs(registerAlertRef, ref)} {...omit(props, 'isWarned')}>
      <FormControlExtendedContextProvider {...props}>{children}</FormControlExtendedContextProvider>
    </ChakraFormControl>
  )
})

FormControl.displayName = 'FormControl'

export type ButtonProps = Omit<ChakraButtonProps, 'disabled'> & {
  disabled?: boolean
  href?: string
  target?: string
  prefetch?: boolean
}

/**
 * Button
 *
 * Extends Chakra's Button with:
 * - `disabled` prop (maps to Chakra's `isDisabled`)
 * - `href` prop to render as a link via `asLink`
 *
 * For Mixpanel tracking, use the composition pattern with `trackedButtonClick`:
 *
 * @example
 * // Button with Mixpanel tracking (composition pattern)
 * import { trackedButtonClick } from '~/third_party/analytics'
 * <Button onClick={trackedButtonClick(handleSubmit, 'new_sow', { sowId: 123 })}>Create SOW</Button>
 *
 * See: https://v1.chakra-ui.com/docs/components/form/button
 *
 * @agent-gotcha variants we promote: primary | secondary | tertiary | deletePrimary | deleteSecondary. ghost/link are deprecated variants.
 */
export const Button = forwardRef<ButtonProps, 'button'>(
  ({ isDisabled, href, disabled, onClick, ...restProps }, ref) => {
    return (
      <ChakraButton {...asLink(href)} ref={ref} isDisabled={disabled || isDisabled} onClick={onClick} {...restProps} />
    )
  }
)

Button.displayName = 'Button'

type CustomIconButtonProps = {
  showTooltip?: boolean
  tooltipProps?: ChakraTooltipProps
} & IconButtonProps

/**
 * Icon Button
 *
 * Custom Icon Button based on Chakra's Icon Button with:
 * - Optional tooltip display (set `showTooltip` and pass `tooltipProps`)
 *
 * For Mixpanel tracking, use the composition pattern with `trackedButtonClick`:
 *
 * @example
 * // Icon button with Mixpanel tracking (composition pattern)
 * import { trackedButtonClick } from '~/third_party/analytics'
 * <IconButton onClick={trackedButtonClick(handleDelete, 'delete_invoice')} aria-label="Delete" icon={<TrashIcon />} />
 *
 * See: https://v1.chakra-ui.com/docs/components/form/icon-button
 *
 * @agent-gotcha always provide aria-label; use showTooltip when the icon action is ambiguous
 */
export const IconButton = forwardRef(
  ({ showTooltip = false, tooltipProps, onClick, ...restProps }: CustomIconButtonProps, ref) => {
    const variantStyles = {
      primary: { color: 'var(--vndly-color-container-light)' },
      deletePrimary: { color: 'var(--vndly-color-container-light)' },
      deleteSecondary: { color: 'var(--vndly-color-button-delete-primary)' }
    }

    const button = (
      <ChakraIconButton ref={ref} sx={variantStyles} onClick={onClick} {...restProps} data-vndly-icon-button="" />
    )

    if (showTooltip) {
      return (
        <ChakraTooltip label={restProps['aria-label']} {...tooltipProps}>
          <chakra.span display={restProps.display}>{button}</chakra.span>
        </ChakraTooltip>
      )
    }

    return button
  }
)

IconButton.displayName = 'IconButton'

export const Heading = forwardRef((props: HeadingProps, ref) => {
  const { textStyle } = props

  // When one of the heading textStyle props are set on a Heading component, use a
  // Box component instead to make sure the custom text style we have defined in our
  // theme is applied.
  let asTag: 'h1' | 'h2' | 'h3' | 'h4' | undefined
  if (textStyle === 'heading1') asTag = 'h1'
  if (textStyle === 'heading2') asTag = 'h2'
  if (textStyle === 'heading3') asTag = 'h3'
  if (textStyle === 'heading4' || textStyle === 'sectionHeading') asTag = 'h4'

  // When any other textStyle prop is set on a Heading component, use the Box component to apply
  // that given text style, otherwise use the Chakra Heading component as it is
  return textStyle ? (
    <Box as={asTag} ref={ref} className="chakra-heading" {...props} />
  ) : (
    <ChakraHeading ref={ref} {...props} />
  )
})

// Use opacity for better css perf
const mutedFade = keyframes`
  from {
    opacity: .25
  }
  to {
    opacity: 1
  }
`
export const Skeleton = forwardRef<SkeletonProps, 'div'>((props, ref) => {
  return <ChakraSkeleton ref={ref} animation={`0.8s ease-out infinite alternate ${mutedFade}`} {...props} />
})

export type TooltipProps = {
  isFocusable?: boolean
  /** Props applied to the button wrapper element. Only used when `isFocusable` is true. */
  containerProps?: React.ComponentProps<typeof Box>
} & ChakraTooltipProps

export const Tooltip = forwardRef<TooltipProps, 'div'>(
  ({ isFocusable = false, containerProps, children, ...props }, ref) => {
    return (
      <ChakraTooltip ref={ref} {...props}>
        {isFocusable ? (
          <Box
            as="button"
            aria-roledescription="tooltip"
            type="button"
            verticalAlign="middle"
            className="focusable"
            {...containerProps}>
            {children}
          </Box>
        ) : (
          children
        )}
      </ChakraTooltip>
    )
  }
)

Tooltip.displayName = 'Tooltip'

export type MenuItemProps = ChakraMenuItemProps

/**
 * MenuItem
 *
 * Extends Chakra's MenuItem.
 *
 * For Mixpanel tracking, wrap the onClick handler with trackedButtonClick:
 * @example
 * import { trackedButtonClick } from '~/third_party/analytics'
 * <MenuItem onClick={trackedButtonClick(handleDelete, 'delete_sow')}>Delete SOW</MenuItem>
 */
export const MenuItem = forwardRef<MenuItemProps, 'button'>(({ onClick, ...restProps }, ref) => {
  return <ChakraMenuItem ref={ref} onClick={onClick} {...restProps} />
})

MenuItem.displayName = 'MenuItem'
